import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    alpha,
    Avatar,
    Box,
    Chip,
    Container,
    Fade,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FormatQuoteOutlinedIcon from "@mui/icons-material/FormatQuoteOutlined";
import { getAllComplains } from "../../../redux/complainRelated/complainHandle";

const headCells = [
    { id: "submitter", label: "Submitted by", width: "22%" },
    { id: "role", label: "Role", width: "10%" },
    { id: "complaint", label: "Complaint", width: "48%" },
    { id: "date", label: "Submitted", width: "14%" },
    { id: "actions", label: "", align: "right", width: "6%" },
];

function TableSkeleton() {
    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        {headCells.map((c) => (
                            <TableCell key={c.id} sx={{ fontWeight: 700 }}>
                                {c.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <TableRow key={i}>
                            <TableCell colSpan={5}>
                                <Skeleton variant="rounded" height={44} sx={{ borderRadius: 1 }} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

const SeeComplains = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { complainsList, loading, error, response } = useSelector((state) => state.complain);
    const { currentUser } = useSelector((state) => state.user);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        if (currentUser?._id) {
            dispatch(getAllComplains(currentUser._id, "Complain"));
        }
    }, [currentUser?._id, dispatch]);

    const rows = useMemo(() => {
        if (!Array.isArray(complainsList) || complainsList.length === 0) return [];
        return [...complainsList]
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .map((complain) => {
                const date = new Date(complain.date);
                const dateLabel =
                    date.toString() !== "Invalid Date" ? date.toLocaleDateString(undefined, { dateStyle: "medium" }) : "—";
                const isTeacher = Boolean(complain.teacherId || complain.teacher);
                const studentId = complain.user?._id || complain.user?.id || complain.userId;
                const rollNum = complain.user?.rollNum;
                const submitterName = isTeacher
                    ? complain.teacher?.name?.trim() || "Unknown teacher"
                    : complain.user?.name?.trim() || "Unknown student";
                return {
                    id: complain._id || complain.id,
                    studentId: isTeacher ? null : studentId,
                    submitterName,
                    roleLabel: isTeacher ? "Teacher" : "Student",
                    detailLabel: isTeacher
                        ? complain.teacher?.email || "—"
                        : rollNum != null && rollNum !== ""
                          ? `Roll ${rollNum}`
                          : "—",
                    complaintText: complain.complaint?.trim() || "—",
                    dateLabel,
                };
            });
    }, [complainsList]);

    const paginated = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const hasRows = rows.length > 0;
    const showEmpty = !loading && !error && !hasRows;
    const showTable = !loading && !error && hasRows;

    return (
        <Box
            component="main"
            sx={{
                minHeight: "100%",
                pb: 6,
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.warning.main, 0.07)} 0%, ${t.palette.grey[50]} 28%)`
                        : alpha(t.palette.common.black, 0.2),
            }}
        >
            <Container maxWidth="lg" sx={{ pt: { xs: 2, sm: 3 } }}>
                <Fade in timeout={380}>
                    <Stack spacing={3}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2.5, sm: 3 },
                                borderRadius: 3,
                                color: "common.white",
                                background: (t) =>
                                    `linear-gradient(120deg, ${t.palette.warning.dark} 0%, ${t.palette.warning.main} 52%, ${alpha(
                                        t.palette.warning.light,
                                        0.88
                                    )} 100%)`,
                                boxShadow: (t) => `0 14px 44px ${alpha(t.palette.warning.main, 0.28)}`,
                            }}
                        >
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                                <Avatar
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        bgcolor: alpha("#fff", 0.2),
                                        color: "inherit",
                                    }}
                                >
                                    <ReportProblemOutlinedIcon sx={{ fontSize: 30 }} />
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="overline" sx={{ opacity: 0.92, letterSpacing: 1.2 }}>
                                        Support inbox
                                    </Typography>
                                    <Typography variant="h4" component="h1" fontWeight={800} sx={{ lineHeight: 1.15 }}>
                                        Complaints
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.92, maxWidth: 620, mt: 0.75 }}>
                                        Student and teacher submissions for your school, newest first. Open a student profile when available.
                                    </Typography>
                                </Box>
                                <Chip
                                    label={`${rows.length} open record${rows.length === 1 ? "" : "s"}`}
                                    sx={{
                                        alignSelf: { xs: "flex-start", sm: "center" },
                                        bgcolor: alpha("#fff", 0.2),
                                        color: "inherit",
                                        fontWeight: 700,
                                        border: "1px solid",
                                        borderColor: alpha("#fff", 0.45),
                                    }}
                                />
                            </Stack>
                        </Paper>

                        {error && (
                            <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                                {error?.message || "Could not load complaints."}
                            </Alert>
                        )}

                        {!error && (
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 3,
                                    overflow: "hidden",
                                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                    boxShadow: (t) => `0 4px 24px ${alpha(t.palette.common.black, 0.06)}`,
                                }}
                            >
                                {loading && (
                                    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <TableSkeleton />
                                    </Box>
                                )}

                                {showEmpty && (
                                    <Box sx={{ py: { xs: 6, sm: 8 }, px: 2, textAlign: "center" }}>
                                        <FormatQuoteOutlinedIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
                                        <Typography variant="h6" fontWeight={700} gutterBottom>
                                            No complaints right now
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440, mx: "auto" }}>
                                            {typeof response === "string"
                                                ? response
                                                : "When students or teachers file complaints, they will appear in this table."}
                                        </Typography>
                                    </Box>
                                )}

                                {showTable && (
                                    <>
                                        <TableContainer>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        {headCells.map((c) => (
                                                            <TableCell
                                                                key={c.id}
                                                                align={c.align}
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
                                                                    whiteSpace: "nowrap",
                                                                    width: c.width,
                                                                }}
                                                            >
                                                                {c.label}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {paginated.map((row) => (
                                                        <TableRow key={row.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                                                            <TableCell>
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <Avatar
                                                                        sx={{
                                                                            width: 34,
                                                                            height: 34,
                                                                            bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                                                                            color: "primary.main",
                                                                        }}
                                                                    >
                                                                        <PersonOutlinedIcon sx={{ fontSize: 18 }} />
                                                                    </Avatar>
                                                                    <Typography variant="body2" fontWeight={700} noWrap title={row.submitterName}>
                                                                        {row.submitterName}
                                                                    </Typography>
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    size="small"
                                                                    label={row.roleLabel}
                                                                    color={row.roleLabel === "Teacher" ? "secondary" : "primary"}
                                                                    variant="outlined"
                                                                />
                                                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                    {row.detailLabel}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ maxWidth: 0 }}>
                                                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                                                                    {row.complaintText}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip size="small" label={row.dateLabel} variant="outlined" />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Tooltip title={row.studentId ? "Open student profile" : "No student link"}>
                                                                    <span>
                                                                        <IconButton
                                                                            size="small"
                                                                            color="primary"
                                                                            disabled={!row.studentId}
                                                                            onClick={() =>
                                                                                row.studentId &&
                                                                                navigate(`/Admin/students/student/${row.studentId}`)
                                                                            }
                                                                            sx={{
                                                                                bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                                                                                "&:hover": {
                                                                                    bgcolor: (t) => alpha(t.palette.primary.main, 0.16),
                                                                                },
                                                                            }}
                                                                        >
                                                                            <VisibilityOutlinedIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <TablePagination
                                            component="div"
                                            count={rows.length}
                                            page={page}
                                            onPageChange={(_, p) => setPage(p)}
                                            rowsPerPage={rowsPerPage}
                                            onRowsPerPageChange={(e) => {
                                                setRowsPerPage(parseInt(e.target.value, 10));
                                                setPage(0);
                                            }}
                                            rowsPerPageOptions={[5, 10, 25, 50]}
                                        />
                                    </>
                                )}
                            </Paper>
                        )}
                    </Stack>
                </Fade>
            </Container>
        </Box>
    );
};

export default SeeComplains;
